/*
 * The Craft response is an untyped, deeply-nested tree, so the traversal helpers
 * legitimately need `any` + unsafe-access; `prefer-inject` is off because this
 * resolver uses constructor `@Inject` DI. Scoped to those rules only — `no-unused-vars`
 * is intentionally NOT disabled so dead code in this central SSR path is still caught.
 */
/* eslint-disable @angular-eslint/prefer-inject, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment */
import { isPlatformServer } from '@angular/common';
import { Inject, Injectable, makeStateKey, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import type { ActivatedRouteSnapshot, RouterStateSnapshot, UrlSegment } from '@angular/router';
import { Router } from '@angular/router';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import { CraftQueryService } from '@core-mkt/services/craft-query/craft-query.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { getBlogPostingSchema } from '@core-mkt/services/schema/blog-posting.schema';
import { getBrandOrganizationId, getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import { getProductCourseSchema } from '@core-mkt/services/schema/product-course-schema';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { normalizeSeoUrl } from '@core-mkt/services/seo-data/normalize-seo-url';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { Response } from 'express';
import { log, SEVERITY } from '../../../shared/lib/logger';
import { ProductService } from '../../services/product/product.service';
import { RESPONSE } from '../../tokens/response.token';
import { BlogPageDataResolver } from './blog-page-data.resolver';
import type { PageDataResponse } from './page-data-response';

// Safety net for extractProductEntries: real Craft pages nest a few levels deep, so any
// walk past this depth indicates pathological data, not content. Kept far above legitimate
// nesting so a capped node can only ever be corrupt data — a skipped product degrades to a
// client-side API refetch (ProductService.getMultiProductData falls through on cache miss).
const MAX_TRAVERSAL_DEPTH = 100;

@Injectable()
export class PageDataResolver {
  constructor(
    @Inject(PLATFORM_ID) private platformId,
    private cqs: CraftQueryService,
    private transferState: TransferState,
    private router: Router,
    private env: EnvService,
    @Optional() @Inject(RESPONSE) private response: Response,
    private blogResolver: BlogPageDataResolver,
    private productService: ProductService,
    private schemaService: SchemaService,
  ) {}

  pageBlogEntriesQueryName: string = 'pageBlogEntries';

  // Replaces the occurrence of double dashes with a forward slash in a string.
  public slugToPath(path: string): string {
    return path.replace('--', '/');
  }

  // Recursively construct the full path of this route.
  recursiveFullPath(route: ActivatedRouteSnapshot, currentUrl = ''): string {
    if (!route.url.length) {
      return currentUrl;
    } else {
      if (currentUrl === '') {
        currentUrl = route.url.join('');
      } else {
        currentUrl = route.url.join('') + '/' + currentUrl;
      }
      return this.recursiveFullPath(route.parent, currentUrl);
    }
  }

  /*
   *   Create craft slug from urlParts for apollo query
   */
  fullPathFromArray(urlParts: Array<UrlSegment>): string {
    //All craft entries requiere a slug. Single's without a prefix should be name 'home'
    if (urlParts.length === 0) {
      return 'home';
    }

    return urlParts.filter((part) => part.path !== '').join('/') || 'home';
  }

  // Each top level key's value is an array by default with Craft's GraphQl function, convert these array values into their first value for convience.
  // For example "Menu": [{}] becomes "Menu": {}.
  processCraftResponse(craftData): object {
    const keys = Object.keys(craftData);
    const processedData = keys.reduce((accum, curr) => {
      if (curr === this.pageBlogEntriesQueryName && craftData[curr] && craftData[curr].length > 0) {
        accum[curr] = craftData[curr];
      } else {
        accum[curr] = craftData[curr][0];
      }
      return accum;
    }, {});

    return processedData;
  }

  /**
   * Extracts all product entries from craft page data for pre-fetching product pricing.
   * Walks the full craft response tree and collects any object carrying an
   * `xgritProductId` — catching product-card, top-section, bundle, pricing-table,
   * and any other surface that references an xGrit product. Deduplicates by
   * productId + coupon so the same product referenced from multiple components
   * is fetched (and later registered as Course schema) only once.
   */
  private extractProductEntries(craftData: any, contextId?: string): CraftXGritProductEntry[] {
    const productEntries: CraftXGritProductEntry[] = [];
    const seen = new Set<string>();
    // Craft/Apollo data can carry shared and even cyclic object references (entries
    // aliased across components, baseComponents linking back to an ancestor — EPS-4074),
    // so the walk must terminate on object identity, not structure. Skipping a revisited
    // reference is lossless: the identical object can only re-derive the same
    // productId|coupon keys already handled on first visit.
    const visited = new WeakSet<object>();
    let depthCapped = false;

    const visit = (node: any, depth: number): void => {
      if (node == null || typeof node !== 'object') return;

      // Depth check must precede the visited marking: a node first reached beyond
      // the cap must stay unmarked so a later, legitimate shallow path to the same
      // shared object still traverses it.
      if (depth > MAX_TRAVERSAL_DEPTH) {
        depthCapped = true;
        return;
      }

      if (visited.has(node)) return;
      visited.add(node);

      if (Array.isArray(node)) {
        node.forEach((child) => visit(child, depth + 1));
        return;
      }

      if (typeof node.xgritProductId === 'string' && node.xgritProductId.length > 0) {
        // Pass the full Craft entry through (preserving disclaimer, title, and any
        // other per-product CMS fields used downstream for Course schema), while
        // normalizing the fields the pricing/dedup paths require.
        const entry: CraftXGritProductEntry = {
          ...node,
          xgritProductId: node.xgritProductId,
          xgritCoupon: node.xgritCoupon || '',
          productState: node.productState || '',
        };
        const key = `${entry.xgritProductId}|${entry.xgritCoupon}`;
        if (!seen.has(key) && this.productService.isProductValid(entry)) {
          seen.add(key);
          productEntries.push(entry);
        }
      }

      for (const key of Object.keys(node)) {
        visit(node[key], depth + 1);
      }
    };

    visit(craftData, 0);

    if (depthCapped) {
      log({
        severity: SEVERITY.WARN,
        message: `extractProductEntries stopped at depth cap (${MAX_TRAVERSAL_DEPTH}); deeper nodes were not traversed`,
        contextId,
        tags: ['PageDataResolver.extractProductEntries'],
      });
    }

    return productEntries;
  }

  /**
   * Pre-fetches product pricing data for all product-card components on the page.
   * Stores the data in TransferState so it's available during SSR and prevents duplicate API calls.
   *
   * `routerUrl` is the Angular router URL (`state.url`), used to build the canonical
   * page URL for each product's `Course.url`. See EPS-4457.
   */
  private async fetchProductData(craftData: any, fullPath: string, routerUrl: string): Promise<void> {
    // Only fetch product data on the server
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    try {
      // Extract all product entries from the page. Inside the try so a traversal
      // failure on malformed Craft data degrades to a logged error instead of
      // rejecting the resolver and blanking the page (EPS-4074).
      const productEntries = this.extractProductEntries(craftData, fullPath);

      if (productEntries.length === 0) {
        // No products on this page, skip fetching
        return;
      }

      // Fetch product data for all products on the page
      const productData: XGritCompleteProduct[] = await this.productService.getMultiProductData(productEntries);

      // Store product data in TransferState with page-specific key
      const PRODUCT_DATA_KEY = makeStateKey<XGritCompleteProduct[]>('product-data-' + fullPath);
      this.transferState.set(PRODUCT_DATA_KEY, productData);

      // Expose the page's products synchronously for components that need them at
      // ngOnInit (e.g. rsa-product's Service schema isRelatedTo) without waiting on
      // the async setProduct() path. See EPS-3923.
      this.productService.setPageProducts(productData);

      // Register a Schema.org Course node for every product on the page. Runs
      // once per product regardless of which components render it — Course
      // nodes describe products, not UI. SchemaService is a no-op on browser
      // (ServerSchemaService only on SSR), so the ld+json <script> is emitted
      // exclusively in the initial server-rendered HTML.
      const courseProvider = {
        id: getBrandOrganizationId(this.env.get.brandConfig.url),
        name: this.env.get.brandConfig.name,
      };
      // Canonical page URL for `Course.url` — the page that DESCRIBES the products.
      // Built once outside the loop since it is identical for every product on the
      // page. Derived from `brandConfig.url` (the public brand domain) rather than
      // `document.URL`, so the internal SSR ingress host can never leak in the way
      // it did in EPS-4230.
      //
      // Host comes from `getBrandSiteUrl` — the same normalizer backing
      // `getBrandOrganizationId` above — so `Course.url` and `provider.@id` can
      // never disagree on the host, and a `brandConfig.url` carrying a protocol
      // or path (which that helper explicitly tolerates) can't produce a
      // malformed `https://https//…` URL. Empty when the brand host is missing:
      // the builder then omits `url` rather than falling back to the checkout
      // link, which sits on a robots-blocked host and cannot serve as a
      // describing page (EPS-4457).
      //
      // This matches the page's `<link rel="canonical">` on self-canonical,
      // non-paginated pages — i.e. every product page today. It deliberately does
      // NOT reproduce the full canonical chain in `ServerSeoDataService`
      // (`overrideCanonical || seoData.canonicalUrl || fullUrl`, then
      // `appendPaginationParam`). If a Craft editor sets a per-entry canonical, or
      // a product page is ever paginated, `Course.url` names the page it is on
      // while the canonical tag points elsewhere. Both are describing pages, so
      // the node stays correct — it just stops being byte-identical.
      const brandSiteUrl = getBrandSiteUrl(this.env.get.brandConfig.url);
      const coursePageUrl = brandSiteUrl ? normalizeSeoUrl(`${brandSiteUrl.replace(/\/$/, '')}${routerUrl}`) : '';
      for (const product of productData) {
        this.schemaService.register(getProductCourseSchema(product, courseProvider, coursePageUrl));
      }

      log({
        severity: SEVERITY.INFO,
        message: `Pre-fetched ${productData.length} product(s) for SSR`,
        contextId: fullPath,
        tags: ['PageDataResolver.fetchProductData'],
      });
    } catch (error) {
      // Log error but don't fail the page load
      log({
        severity: SEVERITY.ERROR,
        message: `Failed to pre-fetch product data: ${error.message}`,
        contextId: fullPath,
        tags: ['PageDataResolver.fetchProductData'],
      });
    }
  }

  /**
   * Builds and registers a BlogPosting schema node for blog detail pages.
   * No-op on browser (SchemaService is server-only) and for non-blog routes.
   * Fields are derived from the entry's top-level metadata (title, dates,
   * blogSummary, featuredEntryImage) by the pure builder in
   * `blog-posting.schema.ts`; that builder returns null when the entry is not
   * a blog or has no headline, so this method can be called unconditionally
   * without leaking nodes onto non-blog pages.
   */
  private registerBlogPostingSchema(craftData: any, routerUrl: string, isBlog: boolean): void {
    if (!isPlatformServer(this.platformId)) return;
    if (!isBlog) return;

    const entryData = craftData?.entryData;
    if (!entryData) return;

    const hostUrl = this.env.get.brandConfig.url;
    const organizationName = this.env.get.brandConfig.name;
    if (!hostUrl || !organizationName) return;

    const siteUrl = `https://${hostUrl}/`;
    const pageUrl = normalizeSeoUrl(`https://${hostUrl}${routerUrl}`);

    this.schemaService.register(
      getBlogPostingSchema({
        entryData,
        pageUrl,
        siteUrl,
        organizationName,
      }),
    );
  }

  async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<PageDataResponse | null> {
    // Filter non-craft local http requests
    const nonCraftHTTPRoutes = [`/${this.env.get.brandConfig.craftSite}-assets`];

    if (nonCraftHTTPRoutes.some((route) => state.url.startsWith(route))) {
      return null;
    }

    // Build the full path from child segments. When a blog parent route (e.g., 'resources')
    // consumes its segment, route.url only contains child segments. We recover the parent
    // prefix from route.parent.url when the route is inside a blog parent route.
    const childPath = this.fullPathFromArray(route.url);
    let fullPath = childPath;

    const isInsideBlogParentRoute = route.parent?.data?.['isBlog'] === true;
    let blogParentPrefix = '';

    if (isInsideBlogParentRoute && route.parent?.url) {
      const parentPath = route.parent.url.map((s) => s.path).join('/');
      blogParentPrefix = parentPath ? parentPath.replace(/\//g, '--') + '--' : '';

      const envBlogPrefix = this.env.get.blogPrefix;
      if (!envBlogPrefix || blogParentPrefix !== envBlogPrefix) {
        fullPath = childPath === 'home' ? parentPath : `${parentPath}/${childPath}`;
      }
    }

    // Store resolver returned data at CRAFT_DATA_KEY in the browser using the transfer state api...
    // so that the browser does not also have to make product API calls.
    const CRAFT_DATA_KEY = makeStateKey<PageDataResponse>('craft-data-' + fullPath);

    // If the transfer state already has the resolver data key, CRAFT_DATA_KEY, then...
    // fetch the data and return return it.
    // If the transfer state does not already have the data, then do the work of the resolver.
    if (this.transferState.hasKey(CRAFT_DATA_KEY)) {
      const craftDataFromState = this.transferState.get<PageDataResponse>(CRAFT_DATA_KEY, null);
      // Remove the transfer key / data so that the next page load will make fresh api calls.
      this.transferState.remove(CRAFT_DATA_KEY);

      return craftDataFromState;
    } else {
      // The token and slug in query parameters are used for Craft's preview mode.
      const { token, slug } = route.queryParams;
      // Page data resolver is used for both normal rendering and rendering of a Craft page preview while content authors edit pages.
      // How we handle preview mode:
      // - /preview?slug=X (no token) = render content at /preview using slug X
      // - /preview?slug=X&token=Y = redirect to /X/?token=Y for live sync with Craft
      // - /X/?token=Y (after redirect) = render draft content at /X/
      let craftPreviewMode = false;
      let craftPreviewModeStage2 = false;
      if (token && slug && fullPath === 'preview') {
        // Token + slug + preview path = redirect to actual path for live sync
        craftPreviewMode = true;
        craftPreviewModeStage2 = true;
      } else if (slug && fullPath === 'preview') {
        // Slug + preview path (no token) = render content at /preview
        craftPreviewMode = true;
      } else if (token) {
        // Token on regular path = already redirected, fetch draft content
        craftPreviewMode = true;
        craftPreviewModeStage2 = true;
      }

      // Redirect to actual path when token is present (for live sync)
      if (craftPreviewMode && craftPreviewModeStage2 && slug && fullPath === 'preview') {
        const previewPath = this.slugToPath(slug as string);

        void this.router.navigate([`/${previewPath}/`], {
          queryParamsHandling: 'merge',
          queryParams: { token: String(token) },
        });
        return null;
      }

      // Use the craft query service to get the page data.
      // The craft entry to query is determined from the full path.
      const site = this.env.get.brandConfig.craftSite;

      // Check if this route is for blog content
      const blogPrefix = this.env.get.blogPrefix;
      //return null if environment is not able to provide site info
      if (!site || blogPrefix === null || blogPrefix === undefined) {
        return null;
      }
      let isBlog = false;
      let queryPath = fullPath;
      if (blogPrefix && blogPrefix.length > 0) {
        if (isInsideBlogParentRoute && blogParentPrefix === blogPrefix) {
          isBlog = true;
        }

        if (!isBlog && fullPath === 'preview' && slug && typeof slug === 'string') {
          isBlog = slug.startsWith(blogPrefix);
        }

        if (isBlog && childPath === 'home') {
          queryPath = 'home';
        }
      }

      if (isPlatformServer(this.platformId) && this.response) {
        this.response.setHeader('x-amz-meta-cache-tag', isBlog ? 'site:blog' : 'site:ecom');
      }

      // Only pass slug to getQuery when we're in preview mode (fullPath === 'preview')
      // Otherwise, slug from query params can interfere with normal routing
      const slugForQuery: string | undefined = fullPath === 'preview' ? (slug as string) : undefined;

      let craftQueryServiceError: Error | undefined;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      let query = await this.cqs
        .getQuery(queryPath, token as string, site, slugForQuery, isBlog)
        .catch((err: Error) => {
          craftQueryServiceError = err;
        });
      // If the query is null, page does not exist in Craft.
      if (query === null) {
        if (isPlatformServer(this.platformId)) {
          log({
            severity: SEVERITY.INFO,
            code: 40401,
            message: `Page not found in Craft: ${fullPath}`,
            brandId: site,
            tags: ['PageDataResolver.resolve'],
          });
          if (this.response) {
            this.response.status(404);
          }
        }
        return null;
      }

      if (craftQueryServiceError) {
        log({
          severity: SEVERITY.ERROR,
          code: 40403,
          message: `getQuery failed for path: ${fullPath} — ${craftQueryServiceError.message}`,
          brandId: site,
          tags: ['PageDataResolver.resolve.getQuery'],
        });

        return null;
      }

      query = query.data;
      let processedData: PageDataResponse = null;

      // Format the craft data a bit for convience.
      const craftData: any = this.processCraftResponse(query);

      // Fetch paginated blog entries if this page has an EntryListings component
      // that needs blog data (check if baseComponents contains entryListings)
      if (this.blogResolver.shouldFetchBlogEntries(craftData)) {
        try {
          await this.blogResolver.fetchBlogEntries(route, site, craftData);
        } catch (error) {
          // Error already logged in BlogPageDataResolver
          // Continue with page rendering even if blog fetch fails
        }
      }

      // Pre-fetch product pricing data for all product-card components on the page
      // This ensures product data is available during SSR and visible in initial HTML
      await this.fetchProductData(craftData, fullPath, state.url);

      // Register BlogPosting schema for blog detail pages. Mirrors fetchProductData:
      // resolver-time registration so the @graph is populated before render.
      this.registerBlogPostingSchema(craftData, state.url, isBlog);

      // If the query was successful, initialize the the 'processedData' object.
      // processedData is what the resolver will return
      if (query && Object.keys(query).length) {
        processedData = {
          craftData,
        };
      }

      // If we are on the server, set the data in the transferState api so that it can get picked up in the browser.
      if (isPlatformServer(this.platformId)) {
        this.transferState.set(CRAFT_DATA_KEY, processedData);
      }

      return processedData;
    }
  }
}
